import { useMutation } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import { apiPath, UNEXPLAINED_FAILURE } from './utils';

/** The flags this story can edit inside a workspace. */
export interface AreaFlagEdit {
  /** "Promote to map" — publishes the area as a layer on Insights maps. */
  shared?: boolean;
  /** False archives the area, true restores it. */
  active?: boolean;
}

export interface AreaFlagsWrite {
  /** True while the write is in flight. */
  saving: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  /** Resolves true when the flags were written. */
  save: (edit: AreaFlagEdit, reason?: string) => Promise<boolean>;
  reset: () => void;
}

interface FlagsVariables {
  edit: AreaFlagEdit;
  reason?: string;
}

// A refused edit, carrying the backend's own wording. A promoted workspace is frozen,
// and that 409 is the one refusal a user will actually meet.
class FlagsRefused extends Error {
  readonly reasons: string[];

  constructor(reasons: string[]) {
    super('Area edit refused');
    this.reasons = reasons;
  }
}

// Reads the reasons off a failed PATCH. The freeze answers with the standard
// ApplicationError envelope — `errors[0].message` — and a bare string list is tolerated
// too, since that costs one branch and the two shapes have been swapped once already.
const toFlagsError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error : new Error('Area edit failed');
  }

  const body = (await error.response.json().catch(() => null)) as {
    errors?: Array<string | { message?: string }>;
  } | null;

  const reasons = (body?.errors ?? [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.message))
    .filter((reason): reason is string => !!reason);

  return new FlagsRefused(reasons);
};

// Editing an area inside its workspace: the map flag and the archive flag, the only two
// this story allows. A reason, where one was given, rides in the PATCH body — the
// backend records it as a Comment on the archive transition, so it lands in the
// activity feed beside the change, the same place a promotion's reason goes.
export const useAreaFlags = (
  areaGeometryId: number | null | undefined
): AreaFlagsWrite => {
  const { mutateAsync, isPending, error, reset } = useMutation<
    void,
    Error,
    FlagsVariables
  >({
    mutationFn: async ({ edit, reason }) => {
      try {
        await api.patch(apiPath(`/area_geometries/${areaGeometryId}`), {
          json: reason ? { ...edit, reason } : edit,
        });
      } catch (requestError) {
        throw await toFlagsError(requestError);
      }
    },
    // Neither flag is safe to repeat blindly, and the freeze is an answer rather than
    // a blip.
    retry: false,
  });

  const save = async (edit: AreaFlagEdit, reason?: string) => {
    try {
      await mutateAsync({ edit, reason });
      return true;
    } catch {
      // The reason is read off `error` below.
      return false;
    }
  };

  const refusal =
    error instanceof FlagsRefused && error.reasons.length > 0
      ? error.reasons.join(' ')
      : null;

  return {
    saving: isPending,
    error: error ? (refusal ?? UNEXPLAINED_FAILURE) : null,
    save,
    reset,
  };
};
